const getIndiaDateParts = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null;
  }

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const values = {};
  parts.forEach((part) => {
    if (part.type !== "literal") values[part.type] = part.value;
  });

  return {
    year: Number(values.year),
    month: Number(values.month),
    day: Number(values.day),
  };
};

const toISODate = (year, month, day) => {
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));

  if (
    date.getUTCFullYear() !== Number(year) ||
    date.getUTCMonth() !== Number(month) - 1 ||
    date.getUTCDate() !== Number(day)
  ) {
    return null;
  }

  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

export const getDate = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (value instanceof Date) {
    const parts = getIndiaDateParts(value);
    return parts ? toISODate(parts.year, parts.month, parts.day) : null;
  }

  const text = String(value).trim();

  if (!text) {
    return null;
  }

  const isoDate = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);

  if (isoDate) {
    return toISODate(isoDate[1], isoDate[2], isoDate[3]);
  }

  const dmy = text.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2,4})$/);

  if (dmy) {
    let year = Number(dmy[3]);

    if (year < 100) {
      year += 2000;
    }

    return toISODate(year, dmy[2], dmy[1]);
  }

  // ISO strings without an explicit offset are treated as local date values.
  const localISO = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})T/);
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(text);

  if (localISO && !hasTimezone) {
    return toISODate(localISO[1], localISO[2], localISO[3]);
  }

  const parsed = new Date(text);
  const parts = getIndiaDateParts(parsed);

  return parts ? toISODate(parts.year, parts.month, parts.day) : null;
};

/*
 * Format dates as DD-MM-YYYY (Indian day-month-year format).
 */
/*
 * Format dates as DD-MMM-YYYY.
 * Example: 01-Oct-2026
 */
export const formatDate = (value) => {
  const isoDate = getDate(value);

  if (!isoDate) {
    return "—";
  }

  const [year, month, day] = isoDate.split("-");

  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];

  return `${day}-${months[Number(month) - 1]}-${year}`;
};

/*
 * One key per material code across MRP, stock, PR, PO, production and allotments:
 * spaces and letter case are ignored, and leading zeros of an all-digit code are
 * dropped, so "000123", "123" and " 123 " are the same material.
 */
export const normalizeMaterial = (value) =>
  String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/^0+(?=\d)(?=\d+$)/, "");

/*
 * Outstanding PR quantity is requested quantity minus quantity already ordered.
 */
export const getRemainingPRQuantity = (pr = {}) => {
  const requested = Number(pr.quantityRequested) || 0;
  const ordered = Number(pr.quantityOrdered) || 0;

  return Math.max(0, requested - ordered);
};

/*
 * Format quantity using Indian number formatting.
 */
export const formatQuantity = (value) => {
  if (value == null) {
    return "—";
  }

  return (Math.round(value * 1000) / 1000).toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  });
};

/*
 * Get number of days between two dates.
 */
export const getDaysDifference = (date1, date2) => {
  // API dates are full timestamps ("...T00:00:00.000+05:30"); reduce them to the India date first.
  const first = new Date(`${getDate(date1)}T00:00:00`);
  const second = new Date(`${getDate(date2)}T00:00:00`);

  return Math.round((first - second) / (1000 * 60 * 60 * 24));
};

/*
 * PR status:
 * - CLOSED: a PO number is present on the PR, a matching PO row exists, or the
 *   backend reports it closed (no longer in the latest Open PR report / deleted).
 * - OPEN: no PO is associated with the PR.
 */
export const getPRStatus = (pr = {}, poRows = []) => {
  const directPO = String(pr.purchaseOrder || "").trim();

  if (directPO || String(pr.status || "").toUpperCase() === "CLOSED") {
    return "CLOSED";
  }

  return findLinkedPO(pr, poRows) ? "CLOSED" : "OPEN";
};

/*
 * The first PO row converted from this PR: same PR number, and the same PR item,
 * material and plant when both sides have them.
 */
export const findLinkedPO = (pr = {}, poRows = []) => {
  const prNumber = String(pr.purchaseRequisition || "")
    .trim()
    .toUpperCase();
  const prItem = String(
    pr.itemOfRequisition || pr.item || pr.requisitionItem || "",
  )
    .trim()
    .toUpperCase();
  const material = String(pr.material || "")
    .trim()
    .toUpperCase();
  const plant = String(pr.plant || "")
    .trim()
    .toUpperCase();

  if (!prNumber || !Array.isArray(poRows)) {
    return undefined;
  }

  // Only POs that reference this PR number can match.
  const candidates = getIndex(poRows, "purchaseRequisition", (po) =>
    String(po.purchaseRequisition || "")
      .trim()
      .toUpperCase(),
  ).get(prNumber);

  return (candidates || []).find((po) => {
    const poPRItem = String(po.requisitionItem || po.itemOfRequisition || "")
      .trim()
      .toUpperCase();
    const poMaterial = String(po.material || "")
      .trim()
      .toUpperCase();
    const poPlant = String(po.plant || "")
      .trim()
      .toUpperCase();

    return (
      (!prItem || !poPRItem || prItem === poPRItem) &&
      (!material || !poMaterial || material === poMaterial) &&
      (!plant || !poPlant || plant === poPlant)
    );
  });
};

/*
 * Rows of an array grouped by a key (in their original order), built once per
 * array and key name, so repeated lookups do not scan the whole array.
 * Rebuilt if the array's length changes.
 */
const indexCache = new WeakMap();

const getIndex = (rows, name, getKey) => {
  let byName = indexCache.get(rows);
  if (!byName) {
    byName = new Map();
    indexCache.set(rows, byName);
  }

  const cached = byName.get(name);
  if (cached && cached.length === rows.length) {
    return cached.index;
  }

  const index = new Map();
  rows.forEach((row) => {
    const key = getKey(row);
    if (!index.has(key)) {
      index.set(key, []);
    }
    index.get(key).push(row);
  });

  byName.set(name, { length: rows.length, index });
  return index;
};

/*
 * PR conversion status uses the same OPEN/CLOSED rule.
 */
export const getPRConversionStatus = (pr = {}, poRows = []) => {
  return getPRStatus(pr, poRows);
};

/*
 * Calculate PR age in days.
 */
export function getPRAge(pr, today) {
  const date = getDate(pr.requisitionDate);

  if (!date) {
    return 0;
  }

  const age = getDaysDifference(today, date);

  return Math.max(0, age);
}

/*
 * Check whether a PR has a linked PO.
 */
export const prHasPO = (pr, poRows = []) => {
  return getPRStatus(pr, poRows) === "CLOSED";
};

/*
 * Get PO balance (open quantity in the PO's order unit).
 * A line closed in SAP (no longer in the latest Open PO report, or deleted) has none.
 */
export const getPOBalance = (po) => {
  if (po.closedInSource) {
    return 0;
  }

  const ordered = Number(po.orderQuantity) || 0;
  const delivered = Number(po.quantityDelivered) || 0;

  return Math.max(0, ordered - delivered);
};

/*
 * Base (stock-keeping) units per order unit, from "Quantity in SKU".
 * 1 when the order unit and stock-keeping unit are the same or unknown.
 */
export const getPOUnitFactor = (po) => {
  const orderUnit = String(po.orderUnit || po.unit || "").trim().toUpperCase();
  const skuUnit = String(po.stockkeepingUnit || "").trim().toUpperCase();
  const ordered = Number(po.orderQuantity) || 0;
  const inSku = Number(po.quantityInSku) || 0;

  if (!orderUnit || !skuUnit || orderUnit === skuUnit || ordered <= 0 || inSku <= 0) {
    return 1;
  }

  return inSku / ordered;
};

/*
 * Open PO quantity in the base unit, the unit used by stock and requirements.
 */
export const getPOOpenBaseQuantity = (po) => getPOBalance(po) * getPOUnitFactor(po);

/*
 * Get PO status.
 */
export const getPOStatus = (po, today) => {
  const balance = getPOBalance(po);

  if (balance <= 0) {
    return "CLOSED";
  }

  const deliveryDate = getDate(po.deliveryDate);

  if (!deliveryDate) {
    return "OPEN";
  }

  if (deliveryDate < today) {
    return "OVERDUE";
  }

  const delivery = new Date(deliveryDate);
  const current = new Date(today);

  const days = Math.round((delivery - current) / (1000 * 60 * 60 * 24));

  if (days <= 7) {
    return "DUE_SOON";
  }

  return "OPEN";
};

/*
 * Create a unique list of materials.
 *
 * Production adjustments are included because a produced item
 * may exist in today's production report even when it is not
 * present in the latest MRP/Stock/PR/PO rows.
 */
export const createMaterials = (
  mrpRows,
  stockRows,
  prRows,
  poRows,
  productionAdjustments = [],
) => {
  const materialMap = new Map();

  /*
   * MRP
   */
  mrpRows.forEach((row) => {
    const key = normalizeMaterial(row.material);

    if (!key) {
      return;
    }

    if (!materialMap.has(key)) {
      materialMap.set(key, {
        key: key,
        code: row.material,
        desc: row.description || "—",
        uom: row.unit || "",
        materialGroup: row.materialGroup || "",
        plant: row.plant || "",
      });
    }
  });

  /*
   * Stock
   */
  stockRows.forEach((row) => {
    const key = normalizeMaterial(row.material);

    if (!key) {
      return;
    }

    if (!materialMap.has(key)) {
      materialMap.set(key, {
        key: key,
        code: row.material,
        desc: row.description || "—",
        uom: row.unit || "",
        materialGroup: "",
        plant: row.plant || "",
      });
    }
  });

  /*
   * PR
   */
  prRows.forEach((row) => {
    const key = normalizeMaterial(row.material);

    if (!key) {
      return;
    }

    if (!materialMap.has(key)) {
      materialMap.set(key, {
        key: key,
        code: row.material,
        desc: row.shortText || "—",
        uom: row.unit || "",
        materialGroup: "",
        plant: row.plant || "",
      });
    }
  });

  /*
   * PO
   */
  poRows.forEach((row) => {
    const key = normalizeMaterial(row.material);

    if (!key) {
      return;
    }

    if (!materialMap.has(key)) {
      materialMap.set(key, {
        key: key,
        code: row.material,
        desc: row.shortText || "—",
        uom: row.unit || "",
        materialGroup: "",
        plant: row.plant || "",
      });
    }
  });

  /*
   * Production Report
   */
  productionAdjustments.forEach((row) => {
    const key = normalizeMaterial(row.material);

    if (!key) {
      return;
    }

    if (!materialMap.has(key)) {
      materialMap.set(key, {
        key: key,
        code: row.material,
        desc: "—",
        uom: "",
        materialGroup: "",
        plant: "",
      });
    }
  });

  const list = Array.from(materialMap.values());

  list.sort((a, b) => {
    if (a.code < b.code) {
      return -1;
    }

    if (a.code > b.code) {
      return 1;
    }

    return 0;
  });

  return list;
};

/*
 * Movement type rules, the same as the backend uses for the PO received quantity:
 * - GR into blocked stock (103) only counts when released (105), so 103 and its
 *   reversal/returns (104, 124, 125) are not counted;
 * - reversals and returns to the vendor (incl. 161) reduce the received quantity;
 *   162 reverses a return, so it adds the quantity back.
 */
const BLOCKED_STOCK_MOVEMENT_TYPES = ["103", "104", "124", "125"];
const NEGATIVE_MOVEMENT_TYPES = [
  "102", "106", "122", "161", "202", "262", "502", "512",
];

/*
 * Goods received after the stock file was taken, per material.
 *
 * The stock file is uploaded monthly while the Delivery Report is uploaded
 * daily, so a receipt posted after the stock date is not in the stock file
 * yet. A receipt posted on or before the stock date is already in it.
 *
 * The stock date is the material's Stock Date, else the latest Stock Date of
 * the file, else the date the stock file was uploaded.
 */
export const getReceiptsAfterStock = (
  deliveryRows = [],
  stockRows = [],
  stockUploadDate = null,
  poRows = [],
) => {
  // Order-unit to base-unit factor per PO + material, to convert receipts entered in the order unit
  const poFactor = {};
  poRows.forEach((po) => {
    const poKey = `${String(po.purchasingDocument || "").trim().toUpperCase()}|${normalizeMaterial(po.material)}`;
    poFactor[poKey] = {
      factor: getPOUnitFactor(po),
      orderUnit: String(po.orderUnit || po.unit || "").trim().toUpperCase(),
    };
  });
  const stockDateByMaterial = {};
  let fileStockDate = null;

  stockRows.forEach((row) => {
    const key = normalizeMaterial(row.material);
    const date = getDate(row.stockDate);

    if (!date) {
      return;
    }

    if (!fileStockDate || date > fileStockDate) {
      fileStockDate = date;
    }

    if (key && (!stockDateByMaterial[key] || date > stockDateByMaterial[key])) {
      stockDateByMaterial[key] = date;
    }
  });

  const fallbackDate = fileStockDate || getDate(stockUploadDate);
  const receipts = {};

  deliveryRows.forEach((row) => {
    const key = normalizeMaterial(row.material);
    const postingDate = getDate(row.postingDate);
    const stockDate = stockDateByMaterial[key] || fallbackDate;
    const quantity = Math.abs(Number(row.quantityInUnitOfEntry) || 0);

    if (!key || !postingDate || !stockDate || quantity === 0) {
      return;
    }

    if (postingDate <= stockDate) {
      return;
    }

    const movement = String(row.movementType || "").trim();

    if (BLOCKED_STOCK_MOVEMENT_TYPES.includes(movement)) {
      return;
    }

    // A receipt entered in the PO's order unit is converted to the base unit of the stock
    const po = poFactor[`${String(row.purchaseOrder || "").trim().toUpperCase()}|${key}`];
    const unitOfEntry = String(row.unitOfEntry || "").trim().toUpperCase();
    const baseQuantity =
      po && po.factor !== 1 && (!unitOfEntry || unitOfEntry === po.orderUnit)
        ? quantity * po.factor
        : quantity;

    const signed = NEGATIVE_MOVEMENT_TYPES.includes(movement)
      ? -baseQuantity
      : baseQuantity;

    if (!receipts[key]) {
      receipts[key] = { qty: 0, count: 0, stockDate };
    }

    receipts[key].qty += signed;
    receipts[key].count += 1;
  });

  return receipts;
};

/*
 * Calculate coverage for every material.
 *
 * Supply:
 * - Current stock
 * - Goods received after the stock date (Delivery Report)
 * - Today's production
 * - Open PO
 * - Approved open PR
 *
 * Demand:
 * - MRP requirements, or the planner's allotted quantity for the
 *   month when allottedRequirements is passed
 * - Production consumption from today's production
 *
 * Production consumption is kept separate from MRP requirement
 * so the actual MRP requirement amount is not changed.
 */
export const calculateCoverage = (
  mrpRows,
  stockRows,
  prRows,
  poRows,
  today,
  productionAdjustments = [],
  allottedRequirements = null,
  deliveryRows = [],
  stockUploadDate = null,
  combinedUploadDate = null,
) => {
  const materialKeys = new Set();
  // On hand is as of the combined report upload, production as of the production report upload.
  // Never later than today, so they still come before today's supply and demand.
  const asOf = (value) => {
    const date = getDate(value);
    return date && date < today ? date : today;
  };
  const stockAsOfDate = asOf(combinedUploadDate);
  const productionAsOfDate = asOf(
    productionAdjustments.map((row) => getDate(row.reportDate)).filter(Boolean).sort().pop(),
  );
  const useAllotment = Array.isArray(allottedRequirements);
  const receiptsByMaterial = getReceiptsAfterStock(
    deliveryRows,
    stockRows,
    stockUploadDate,
    poRows,
  );

  const stockByMaterial = {};
  const poByMaterial = {};
  const prByMaterial = {};
  const mrpByMaterial = {};
  const productionByMaterial = {};

  const poPrKeys = new Set();

  /*
   * MRP
   */
  const requirementRows = useAllotment
    ? allottedRequirements.map((row) => ({
        material: row.material,
        reqQty: row.allottedQty,
        reqDate: row.month ? `${row.month}-01` : today,
        element: `Planner allotment ${row.month || ""}`.trim(),
      }))
    : mrpRows;

  requirementRows.forEach((row) => {
    const key = normalizeMaterial(row.material);

    if (!key) {
      return;
    }

    materialKeys.add(key);

    if (!mrpByMaterial[key]) {
      mrpByMaterial[key] = [];
    }

    mrpByMaterial[key].push(row);
  });

  /*
   * Stock
   */
  stockRows.forEach((row) => {
    const key = normalizeMaterial(row.material);

    if (!key) {
      return;
    }

    materialKeys.add(key);

    if (!stockByMaterial[key]) {
      stockByMaterial[key] = [];
    }

    stockByMaterial[key].push(row);
  });

  /*
   * PO
   */
  poRows.forEach((row) => {
    const key = normalizeMaterial(row.material);

    if (!key) {
      return;
    }

    materialKeys.add(key);

    if (!poByMaterial[key]) {
      poByMaterial[key] = [];
    }

    poByMaterial[key].push(row);

    poPrKeys.add(`${row.purchaseRequisition}-${row.requisitionItem}`);
  });

  /*
   * PR
   */
  prRows.forEach((row) => {
    const key = normalizeMaterial(row.material);

    if (!key) {
      return;
    }

    materialKeys.add(key);

    if (!prByMaterial[key]) {
      prByMaterial[key] = [];
    }

    prByMaterial[key].push(row);
  });

  /*
   * Today's production.
   *
   * Example:
   *
   * Item-001     +10
   * MAT-001      -20
   * MAT-002      -30
   *
   * This data comes from the backend after recursive BOM
   * calculation.
   */
  productionAdjustments.forEach((row) => {
    const key = normalizeMaterial(row.material);

    if (!key) {
      return;
    }

    materialKeys.add(key);

    if (!productionByMaterial[key]) {
      productionByMaterial[key] = {
        producedQty: 0,
        consumedQty: 0,
      };
    }

    productionByMaterial[key].producedQty += Number(row.producedQty) || 0;

    productionByMaterial[key].consumedQty += Number(row.consumedQty) || 0;
  });

  const coverageResults = [];

  materialKeys.forEach((key) => {
    const events = [];

    const materialStock = stockByMaterial[key] || [];
    const materialPOs = poByMaterial[key] || [];
    const materialPRs = prByMaterial[key] || [];
    const materialMRPs = mrpByMaterial[key] || [];

    /*
     * Current stock
     */
    materialStock.forEach((row) => {
      const quantity = Number(row.unrestricted) || 0;

      if (quantity > 0) {
        events.push({
          type: "STOCK",
          date: stockAsOfDate,
          ref: "On hand",
          inQ: quantity,
          outQ: 0,
        });
      }
    });

    /*
     * Goods received after the stock date are physically in stock now,
     * but not yet in the monthly stock file.
     */
    const receipt = receiptsByMaterial[key];

    if (receipt && receipt.qty !== 0) {
      events.push({
        type: "RECEIPT",
        date: today,
        ref: `Received after stock date ${formatDate(receipt.stockDate)} (${receipt.count} GR)`,
        inQ: receipt.qty > 0 ? receipt.qty : 0,
        outQ: receipt.qty < 0 ? -receipt.qty : 0,
      });
    }

    const materialProduction = productionByMaterial[key] || {
      producedQty: 0,
      consumedQty: 0,
    };

    /*
     * Today's production is available today.
     */
    if (materialProduction.producedQty > 0) {
      events.push({
        type: "PRODUCTION",
        date: productionAsOfDate,
        ref: "Production till date",
        inQ: materialProduction.producedQty,
        outQ: 0,
      });
    }

    /*
     * BOM consumption caused by today's production.
     */
    if (materialProduction.consumedQty > 0) {
      events.push({
        type: "PRODUCTION_CONSUMPTION",
        date: productionAsOfDate,
        ref: "Production consumption",
        inQ: 0,
        outQ: materialProduction.consumedQty,
      });
    }

    /*
     * Open PO
     */
    materialPOs.forEach((row) => {
      // Open quantity in the base unit; none once the line is closed in SAP
      const quantity = getPOOpenBaseQuantity(row);

      if (quantity <= 0) {
        return;
      }

      // Delivery date = the linked PR's date, else PO date + lead time (from the backend).
      // Without either it is still counted today, and flagged.
      let deliveryDate = getDate(row.deliveryDate);
      let note = "";

      if (!deliveryDate) {
        deliveryDate = today;
        note = " (no delivery date)";
      } else if (row.deliveryDateBasis === "LEAD_TIME") {
        note = " (expected by lead time)";
      }

      if (deliveryDate < today) {
        deliveryDate = today;
      }

      events.push({
        type: "OPEN_PO",
        date: deliveryDate,
        ref: `${row.purchasingDocument || ""}/${row.item || ""}${note}`,
        inQ: quantity,
        outQ: 0,
      });
    });

    /*
     * Open PR
     */
    materialPRs.forEach((row) => {
      /*
       * Same quantity as the PR table: requested minus already ordered.
       */
      const quantity = getRemainingPRQuantity(row);

      if (quantity <= 0) {
        return;
      }

      /*
       * Closed PR is ignored.
       */
      if (row.processingStatus === "Closed") {
        return;
      }

      /*
       * PR already converted to PO is ignored, using the same rule as the
       * PR status badge (PO number on the PR, or a PO linked to it).
       */
      const converted =
        poPrKeys.has(`${row.purchaseRequisition}-${row.item}`) ||
        getPRStatus(row, poRows) === "CLOSED";

      if (converted) {
        return;
      }

      /*
       * Awaiting-release PR does not count as secured supply.
       */
      if (row.processingStatus === "Awaiting Release") {
        return;
      }

      const requiredDate = getDate(row.deliveryDate) || today;

      events.push({
        type: "OPEN_PR",
        date: requiredDate,
        ref: `${row.purchaseRequisition || ""}/${row.item || ""}`,
        inQ: quantity,
        outQ: 0,
      });
    });

    /*
     * MRP requirements
     */
    materialMRPs.forEach((row) => {
      const quantity = Number(row.reqQty) || 0;

      if (quantity <= 0) {
        return;
      }

      let requirementDate = getDate(row.reqDate) || today;

      /*
       * The monthly allotment is needed from today once the month
       * has started.
       */
      if (useAllotment && requirementDate < today) {
        requirementDate = today;
      }

      events.push({
        type: "REQUIREMENT",
        date: requirementDate,
        ref: row.element || "MRP demand",
        inQ: 0,
        outQ: quantity,
      });
    });

    /*
     * Sort events by date.
     *
     * Same day priority:
     * STOCK
     * RECEIPT
     * PRODUCTION
     * OPEN_PO
     * OPEN_PR
     * PRODUCTION_CONSUMPTION
     * REQUIREMENT
     */
    events.sort((a, b) => {
      if (a.date < b.date) {
        return -1;
      }

      if (a.date > b.date) {
        return 1;
      }

      const order = {
        STOCK: 1,
        RECEIPT: 1.5,
        PRODUCTION: 2,
        OPEN_PO: 3,
        OPEN_PR: 4,
        PRODUCTION_CONSUMPTION: 5,
        REQUIREMENT: 6,
      };

      return (order[a.type] || 99) - (order[b.type] || 99);
    });

    /*
     * Running balance
     */
    let balance = 0;
    let wentNegative = false;

    const timeline = [];
    const shortages = [];

    events.forEach((event, index) => {
      const previousBalance = balance;

      balance += event.inQ;
      balance -= event.outQ;

      timeline.push({
        seq: index + 1,
        date: event.date,
        type: event.type,
        ref: event.ref,
        inQ: event.inQ,
        outQ: event.outQ,
        balance: balance,
      });

      if (balance < 0) {
        wentNegative = true;

        const shortage = Math.min(previousBalance, 0) - balance;

        if (shortage > 0) {
          shortages.push({
            date: event.date,
            qty: shortage,
          });
        }
      }
    });

    /*
     * Coverage status
     */
    let status = "COVERED";

    if (wentNegative && balance < 0) {
      status = "SHORT";
    } else if (wentNegative) {
      status = "AT_RISK";
    }

    coverageResults.push({
      key: key,
      status: status,

      /*
       * Only actual MRP demand is included here.
       * Production consumption is internal consumption,
       * so it does not increase totalRequirement.
       */
      totalRequirement: events.reduce(
        (total, event) =>
          total + (event.type === "REQUIREMENT" ? event.outQ : 0),
        0,
      ),

      /*
       * Supply includes:
       * STOCK
       * RECEIPT (net of returns after the stock date)
       * PRODUCTION
       * OPEN_PO
       * OPEN_PR
       */
      totalSupply: events.reduce(
        (total, event) =>
          total +
          (["STOCK", "PRODUCTION", "OPEN_PO", "OPEN_PR"].includes(event.type)
            ? event.inQ
            : event.type === "RECEIPT"
              ? event.inQ - event.outQ
              : 0),
        0,
      ),

      finalBalance: balance,
      shortageQty: Math.max(0, -balance),

      firstShortageDate: shortages.length > 0 ? shortages[0].date : null,

      timeline: timeline,
      shortages: shortages,
    });
  });

  return coverageResults;
};

/*
 * Get coverage result for a material.
 */
export const getMaterialResult = (results, key) => {
  return getIndex(results, "key", (result) => result.key).get(key)?.[0];
};

/*
 * Get material information.
 */
export const getMaterial = (materials, key) => {
  return getIndex(materials, "key", (material) => material.key).get(key)?.[0];
};

/*
 * Get number of materials by coverage status.
 */
export const getCoverageCounts = (results) => {
  const shortCount = results.filter(
    (result) => result.status === "SHORT",
  ).length;

  const riskCount = results.filter(
    (result) => result.status === "AT_RISK",
  ).length;

  const coveredCount = results.filter(
    (result) => result.status === "COVERED",
  ).length;

  return {
    shortCount,
    riskCount,
    coveredCount,
  };
};

/*
 * Get open and closed PR line counts.
 */
export const getOpenPRLines = (prs, poRows = []) => {
  return prs.filter((pr) => getPRStatus(pr, poRows) === "OPEN").length;
};

export const getClosedPRLines = (prs, poRows = []) => {
  return prs.filter((pr) => getPRStatus(pr, poRows) === "CLOSED").length;
};

/*
 * Get overdue PO count.
 */
export const getOverduePOCount = (pos, today) => {
  return pos.filter((po) => getPOStatus(po, today) === "OVERDUE").length;
};

/*
 * Get PO due within 7 days.
 */
export const getDueSoonPOCount = (pos, today) => {
  return pos.filter((po) => getPOStatus(po, today) === "DUE_SOON").length;
};

/*
 * Get POs which still have quantity remaining.
 */
export const getPOsWithBalance = (pos) => {
  return pos.filter((po) => getPOBalance(po) > 0).length;
};

/*
 * Get the next shortages.
 */
export const getNextShortages = (results) => {
  return results
    .filter((result) => result.firstShortageDate)
    .sort((a, b) => {
      if (a.firstShortageDate < b.firstShortageDate) {
        return -1;
      }

      if (a.firstShortageDate > b.firstShortageDate) {
        return 1;
      }

      return 0;
    })
    .slice(0, 7);
};

/*
 * Get the 10-week coverage cells for a material.
 */
export const getCoverageWeeks = (result, today) => {
  if (!result) {
    return [];
  }

  const cells = [];

  for (let week = 0; week < 10; week++) {
    /*
     * "YYYY-MM-DD" parses as UTC midnight, so add days in UTC too.
     * Mixing local and UTC can move a week boundary by a day.
     */
    const startDate = new Date(today);

    startDate.setUTCDate(startDate.getUTCDate() + week * 7);

    const endDate = new Date(today);

    endDate.setUTCDate(endDate.getUTCDate() + week * 7 + 6);

    const from = startDate.toISOString().slice(0, 10);

    const to = endDate.toISOString().slice(0, 10);

    const events = result.timeline.filter(
      (item) => item.date >= from && item.date <= to,
    );

    let className = "no";

    if (events.length > 0) {
      let lowestBalance = events[0].balance;

      events.forEach((event) => {
        if (event.balance < lowestBalance) {
          lowestBalance = event.balance;
        }
      });

      const hasRequirement = events.some(
        (event) => event.type === "REQUIREMENT",
      );

      if (lowestBalance < 0) {
        if (result.status === "SHORT") {
          className = "sh";
        } else {
          className = "rk";
        }
      } else if (hasRequirement) {
        className = "ok";
      }
    }

    cells.push({
      className: className,
      title: `Week ${week + 1} · ${formatDate(from)}`,
    });
  }

  return cells;
};
